import { useEffect, useState } from "react";
import { api, resolveImg, safeUrl } from "../lib/api";
import { X } from "lucide-react";

/**
 * Full-screen geo-targeted ad shown after a greeting is sent.
 * The whole ad image is visible (object-contain). A 10s countdown runs,
 * then it turns into a Dismiss button.
 */
export function AdModal({ open, onClose }) {
  const [ad, setAd] = useState(null);
  const [location, setLocation] = useState(null);
  const [canClose, setCanClose] = useState(false);
  const [seconds, setSeconds] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setCanClose(false);
    setSeconds(10);
    api.get("/ads/serve").then(({ data }) => {
      setAd(data.ad);
      setLocation(data.location);
    }).finally(() => setLoading(false));

    const interval = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(interval);
          setCanClose(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  if (!open) return null;

  const handleClick = () => {
    if (ad) {
      api.post(`/ads/${ad.id}/click`).catch(() => {});
      const url = safeUrl(ad.link_url);
      if (url) window.open(url, "_blank", "noopener");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A] flex flex-col" data-testid="ad-modal">
      {/* Top bar: Sponsored label + countdown/dismiss */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <span className="text-xs font-bold uppercase tracking-widest bg-[#F9F871] text-[#0A0A0A] px-3 py-1.5 rounded-full border-2 border-[#0A0A0A]">
          Sponsored
        </span>
        {canClose ? (
          <button
            data-testid="ad-modal-close"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A0A0A] bg-white px-4 py-1.5 rounded-full border-2 border-[#0A0A0A] hover:-translate-y-0.5 transition-transform"
          >
            <X className="w-4 h-4" /> Dismiss
          </button>
        ) : (
          <span
            data-testid="ad-modal-countdown"
            className="text-sm font-bold uppercase tracking-widest tabular-nums text-white bg-white/15 px-4 py-1.5 rounded-full border-2 border-white/30"
          >
            Dismiss in {seconds}s
          </span>
        )}
      </div>

      {/* Creative fills the rest of the screen; whole image stays visible */}
      {loading ? (
        <div className="flex-1 grid place-items-center text-white font-semibold">Loading…</div>
      ) : ad ? (
        <button onClick={handleClick} data-testid="ad-modal-creative" className="flex-1 min-h-0 flex flex-col text-left">
          {ad.image_url && (
            <img
              src={resolveImg(ad.image_url)}
              alt={ad.headline}
              className="flex-1 min-h-0 w-full object-contain"
            />
          )}
          <div className="shrink-0 p-5 sm:p-6 text-center bg-[#FDFBF7] border-t-2 border-[#0A0A0A]">
            <div className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">{ad.business_name}</div>
            <h3 className="text-2xl sm:text-3xl font-display font-bold mt-1">{ad.headline}</h3>
            <span className="nb-btn bg-[#FFB5A7] px-6 py-3 mt-4 inline-flex">{ad.cta_text}</span>
          </div>
        </button>
      ) : (
        <div className="flex-1 grid place-items-center px-6">
          <div className="nb-card nb-shadow-lg bg-[#FDFBF7] p-8 text-center max-w-md">
            <h3 className="text-2xl font-display font-bold">Your ad could be here 👋</h3>
            <p className="text-[#4A4A4A] mt-2">
              Reach thousands of happy people {location?.city ? `in ${location.city}` : "near you"}.
            </p>
            <a href="/advertise" className="nb-btn bg-[#A8E6CF] px-6 py-3 mt-4 inline-flex">Advertise with us</a>
          </div>
        </div>
      )}
    </div>
  );
}
